import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, Pencil, Trash2, X, Eye, EyeOff, Phone, MessageCircle, Save, Search, Link2, Plus, UserPlus, ChevronDown, ChevronUp, Smartphone, Send, Copy, Check, Camera, Upload, User, Mail, Baby } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import Navigation from '../components/Navigation';
import { getAdminHeaders } from '../lib/auth';
import FormularioFamilia from '../components/FormularioFamilia';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentRow = ({ parent, openEdit, handleDelete }) => (
  <div className="flex items-start justify-between py-2">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-sm text-gray-800">{parent.nome || 'Sem nome'}</span>
        {parent.tipo && <span className="bg-[#E1ECE5] text-[#1A4D2E] px-1.5 py-0.5 rounded text-[10px] font-bold">{parent.tipo}</span>}
        {parent.source === 'portal' && <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded text-[10px] font-bold">Portal</span>}
      </div>
      {parent.celular && (
        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
          <Phone className="w-3 h-3" /> {parent.celular}
        </p>
      )}
    </div>
    <div className="flex items-center gap-1 shrink-0">
      {parent.celular && (
        <button onClick={() => { const num = parent.celular.replace(/\D/g, ''); window.open(`https://wa.me/55${num.startsWith('55') ? num.slice(2) : num}`, '_blank'); }}
          className="bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-lg px-2 py-1 text-[10px] font-semibold flex items-center gap-1" data-testid={`whatsapp-${parent.id}`}>
          <MessageCircle className="w-3 h-3" />
        </button>
      )}
      <button onClick={() => openEdit(parent)} data-testid={`edit-parent-${parent.id}`}
        className="text-gray-400 hover:text-[#1A4D2E] hover:bg-[#F2F7F4] rounded-full p-1.5 transition-all" title="Editar">
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button data-testid={`delete-parent-${parent.id}`}
            className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full p-1.5 transition-all" title="Excluir">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir responsavel</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>{parent.nome}</strong>?
              {parent.source === 'portal' ? ' O login sera removido permanentemente.' : ' Os dados serao removidos das fichas das criancas vinculadas.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(parent)} className="bg-red-500 hover:bg-red-600">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  </div>
);

/* ── Child Selector (multiselect with phone input) ── */
const ChildSelector = ({ allChildren, selectedIds, onToggle, celularesFilhos, onCelularChange }) => {
  const [childSearch, setChildSearch] = useState('');
  const [expanded, setExpanded] = useState(false);

  const q = childSearch.toLowerCase().trim();
  const filtered = q ? allChildren.filter(c => (c.nome || '').toLowerCase().includes(q) || (c.codigo || '').includes(q)) : allChildren;

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">Vincular Filhos</label>

      {/* Selected children with phone input */}
      {selectedIds.length > 0 && (
        <div className="space-y-2 mb-2">
          {selectedIds.map(cid => {
            const child = allChildren.find(c => c.id === cid);
            if (!child) return null;
            return (
              <div key={cid} className="flex items-center gap-2 bg-[#F2F7F4] rounded-lg p-2">
                <span className="text-sm font-semibold text-[#1A4D2E] flex-1 min-w-0 truncate">{child.nome} {child.codigo && <span className="text-gray-400 text-xs">({child.codigo})</span>}</span>
                <div className="flex items-center gap-1">
                  <Smartphone className="w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="tel"
                    inputMode="numeric"
                    placeholder="Cel. filho"
                    value={celularesFilhos[cid] || ''}
                    onChange={e => onCelularChange(cid, e.target.value)}
                    className="w-28 border border-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-[#1A4D2E] outline-none"
                    data-testid={`child-phone-${cid}`}
                  />
                </div>
                <button onClick={() => onToggle(cid)} className="text-red-400 hover:text-red-600 p-1" data-testid={`remove-child-${cid}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Dropdown toggle */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between border border-dashed border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-500 hover:border-[#1A4D2E] hover:text-[#1A4D2E] transition-all"
        data-testid="toggle-child-selector"
      >
        <span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Selecionar criancas</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="mt-2 border border-gray-200 rounded-lg bg-white max-h-48 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input
              value={childSearch}
              onChange={e => setChildSearch(e.target.value)}
              placeholder="Buscar crianca..."
              className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-[#1A4D2E] outline-none"
              data-testid="child-search-input"
            />
          </div>
          <div className="max-h-36 overflow-y-auto divide-y divide-gray-50">
            {filtered.length === 0 ? (
              <p className="text-xs text-gray-400 p-3 text-center">Nenhuma crianca encontrada</p>
            ) : filtered.map(child => {
              const isSelected = selectedIds.includes(child.id);
              return (
                <button
                  key={child.id}
                  type="button"
                  onClick={() => onToggle(child.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[#F2F7F4] transition-all ${isSelected ? 'bg-[#E1ECE5]' : ''}`}
                  data-testid={`select-child-${child.id}`}
                >
                  <span className="font-medium text-gray-700">{child.nome} {child.codigo && <span className="text-gray-400 text-xs">({child.codigo})</span>}</span>
                  {isSelected && <span className="text-[#1A4D2E] text-xs font-bold">Vinculado</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const GestaoPais = () => {
  const navigate = useNavigate();
  const [parents, setParents] = useState([]);
  const [allChildren, setAllChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingParent, setEditingParent] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState({ nome: '', celular: '', email: '', password: '' });
  const [createForm, setCreateForm] = useState({ nome: '', celular: '', email: '', password: '' });
  const [createChildIds, setCreateChildIds] = useState([]);
  const [createCelularesFilhos, setCreateCelularesFilhos] = useState({});
  const [editChildIds, setEditChildIds] = useState([]);
  const [editCelularesFilhos, setEditCelularesFilhos] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteMsg, setInviteMsg] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const editRef = useRef(null);
  const createRef = useRef(null);

  const APP_URL = process.env.REACT_APP_BACKEND_URL;
  const CADASTRO_LINK = `${APP_URL}/app-pais/cadastro`;
  const DEFAULT_INVITE_MSG = `Ola! Voce foi convidado(a) para o Portal da Familia do Ministerio Raizes.\n\nCadastre-se pelo link abaixo para acompanhar as atividades e a pontuacao do seu filho(a):\n\n${CADASTRO_LINK}\n\nE rapido e facil!`;

  useEffect(() => { fetchParents(); fetchChildren(); }, []);

  const fetchParents = async () => {
    try {
      const res = await axios.get(`${API}/admin/parents`, { headers: getAdminHeaders() });
      setParents(res.data || []);
    } catch { toast.error('Erro ao carregar responsaveis'); }
    setLoading(false);
  };

  const fetchChildren = async () => {
    try {
      const res = await axios.get(`${API}/admin/children-list`, { headers: getAdminHeaders() });
      setAllChildren(res.data || []);
    } catch { /* silently fail */ }
  };

  const openEdit = async (parent) => {
    setShowCreateForm(false);
    // Fetch full family data
    try {
      const res = await axios.get(`${API}/admin/parents/${parent.id}`, { headers: getAdminHeaders() });
      const full = res.data;
      const resps = full.responsaveis || [{ nome: full.nome, celular: full.celular, parentesco: full.parentesco || '', email: full.email || '' }];
      const filhos = full.filhos || [];
      setEditingParent({ ...full, editResps: resps, editFilhos: filhos });
    } catch {
      // Fallback to what we have
      const resps = parent.responsaveis || [{ nome: parent.nome, celular: parent.celular, parentesco: parent.parentesco || '', email: parent.email || '' }];
      setEditingParent({ ...parent, editResps: resps, editFilhos: parent.filhos || [] });
    }
    setForm({ password: '' });
    setShowPassword(false);
    setTimeout(() => editRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const closeEdit = () => setEditingParent(null);

  const updateEditResp = (idx, field, val) => {
    setEditingParent(prev => {
      const resps = [...prev.editResps];
      resps[idx] = { ...resps[idx], [field]: val };
      return { ...prev, editResps: resps };
    });
  };
  const addEditResp = () => {
    if (editingParent.editResps.length >= 3) return;
    setEditingParent(prev => ({ ...prev, editResps: [...prev.editResps, { nome: '', celular: '', parentesco: '', email: '' }] }));
  };
  const removeEditResp = (idx) => {
    if (editingParent.editResps.length <= 1) return;
    setEditingParent(prev => ({ ...prev, editResps: prev.editResps.filter((_, i) => i !== idx) }));
  };
  const updateEditFilho = (idx, field, val) => {
    setEditingParent(prev => {
      const filhos = [...prev.editFilhos];
      filhos[idx] = { ...filhos[idx], [field]: val };
      return { ...prev, editFilhos: filhos };
    });
  };
  const removeEditFilho = (idx) => {
    setEditingParent(prev => ({ ...prev, editFilhos: prev.editFilhos.filter((_, i) => i !== idx) }));
  };

  const handleUploadPhoto = async (childId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/children/upload-photo?child_id=${childId}`, formData, {
        headers: { ...getAdminHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setEditingParent(prev => ({
        ...prev,
        editFilhos: prev.editFilhos.map(f => f.id === childId ? { ...f, foto_url: res.data.foto_url } : f)
      }));
      toast.success('Foto atualizada!');
    } catch (e) { toast.error('Erro ao enviar foto'); }
  };

  const [showAddChildEdit, setShowAddChildEdit] = useState(false);
  const [newChildEdit, setNewChildEdit] = useState({ nome: '', data_nascimento: '', sexo: 'M' });

  const handleAddChildEdit = async () => {
    if (!newChildEdit.nome.trim() || !newChildEdit.data_nascimento) { toast.error('Nome e data sao obrigatorios'); return; }
    setSaving(true);
    try {
      // Create child via admin endpoint
      const res = await axios.post(`${API}/children`, {
        nome: newChildEdit.nome, data_nascimento: newChildEdit.data_nascimento, sexo: newChildEdit.sexo,
        pai: editingParent.editResps.find(r => r.parentesco === 'Pai')?.nome || '',
        celular_pai: editingParent.editResps.find(r => r.parentesco === 'Pai')?.celular || '',
        mae: editingParent.editResps.find(r => r.parentesco === 'Mãe' || r.parentesco === 'Mae')?.nome || '',
        celular_mae: editingParent.editResps.find(r => r.parentesco === 'Mãe' || r.parentesco === 'Mae')?.celular || '',
      }, { headers: getAdminHeaders() });
      const newChild = res.data;
      // Link to parent
      await axios.put(`${API}/admin/parents/${editingParent.id}`, {
        child_ids: [...(editingParent.child_ids || []), newChild.id]
      }, { headers: getAdminHeaders() });
      // Update local state
      setEditingParent(prev => ({
        ...prev,
        child_ids: [...(prev.child_ids || []), newChild.id],
        editFilhos: [...prev.editFilhos, { ...newChild, id: newChild.id }]
      }));
      setNewChildEdit({ nome: '', data_nascimento: '', sexo: 'M' });
      setShowAddChildEdit(false);
      toast.success(`${newChild.nome} adicionado(a)!`);
      fetchChildren();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao adicionar'); }
    setSaving(false);
  };

  const handleSave = async () => {
    if (!editingParent.editResps[0]?.nome?.trim()) { toast.error('Nome do responsavel e obrigatorio'); return; }
    setSaving(true);
    try {
      const body = {
        responsaveis: editingParent.editResps,
        filhos: editingParent.editFilhos.map(f => ({
          id: f.id, nome: f.nome, data_nascimento: f.data_nascimento, sexo: f.sexo,
          celular_crianca: f.celular_crianca || '', observacoes: f.observacoes || '',
        })),
      };
      if (form.password) body.password = form.password;
      await axios.put(`${API}/admin/parents/${editingParent.id}`, body, { headers: getAdminHeaders() });
      toast.success('Familia atualizada!');
      closeEdit();
      fetchParents();
      fetchChildren();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    setSaving(false);
  };

  const handleDelete = async (parent) => {
    try {
      let url = `${API}/admin/parents/${parent.id}`;
      if (parent.source === 'crianca') {
        const params = new URLSearchParams();
        if (parent.tipo) params.append('tipo', parent.tipo);
        if (parent.celular) params.append('old_celular', parent.celular);
        if (parent.nome) params.append('old_nome', parent.nome);
        url += `?${params.toString()}`;
      }
      await axios.delete(url, { headers: getAdminHeaders() });
      toast.success('Responsavel excluido!');
      fetchParents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao excluir'); }
  };

  const openCreateForm = () => {
    setEditingParent(null);
    setCreateForm({ nome: '', celular: '', email: '', password: '' });
    setCreateChildIds([]);
    setCreateCelularesFilhos({});
    setShowCreatePassword(false);
    setShowCreateForm(true);
    setTimeout(() => createRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleCreate = async (data) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/families/register-admin`, data, { headers: getAdminHeaders() });
      toast.success('Familia cadastrada com sucesso!');
      // Open WhatsApp links to send password
      if (res.data?.whatsapp_links?.length > 0) {
        res.data.whatsapp_links.forEach((link, i) => {
          setTimeout(() => window.open(link, '_blank'), i * 500);
        });
        toast.success('Links do WhatsApp abertos para enviar a senha');
      }
      setShowCreateForm(false);
      fetchParents();
      fetchChildren();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao cadastrar'); }
    setSaving(false);
  };

  const handleMigrateFamilies = async () => {
    try {
      const res = await axios.post(`${API}/families/migrate-unified`, {}, { headers: getAdminHeaders() });
      toast.success(res.data.message || 'Migração concluída');
      fetchParents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro na migração'); }
  };

  const toggleCreateChild = (childId) => {
    setCreateChildIds(prev => prev.includes(childId) ? prev.filter(id => id !== childId) : [...prev, childId]);
  };

  const toggleEditChild = (childId) => {
    setEditChildIds(prev => prev.includes(childId) ? prev.filter(id => id !== childId) : [...prev, childId]);
  };

  const openInvite = (phone = '') => {
    setInvitePhone(phone);
    setInviteMsg(DEFAULT_INVITE_MSG);
    setLinkCopied(false);
    setShowInvite(true);
  };

  const sendInviteWhatsApp = () => {
    const num = invitePhone.replace(/\D/g, '');
    if (!num || num.length < 10) { toast.error('Informe um celular valido'); return; }
    const fullNum = num.startsWith('55') ? num : `55${num}`;
    const encoded = encodeURIComponent(inviteMsg);
    window.open(`https://wa.me/${fullNum}?text=${encoded}`, '_blank');
    toast.success('Convite enviado via WhatsApp!');
    setShowInvite(false);
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(CADASTRO_LINK).then(() => {
      setLinkCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setLinkCopied(false), 2000);
    }).catch(() => toast.error('Erro ao copiar'));
  };

  // No more grouping - all items are single family records
  const buildFamilyView = (parentsList) => {
    return parentsList.map(p => ({ type: 'single', id: p.id, parent: p }));
  };

  if (loading) return <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center"><p className="text-gray-500">Carregando...</p></div>;

  const q = search.toLowerCase().trim();
  const filtered = q ? parents.filter(p =>
    (p.nome || '').toLowerCase().includes(q) ||
    (p.celular || '').includes(q) ||
    (p.filhos || []).some(f => (f.nome || '').toLowerCase().includes(q))
  ) : parents;

  const items = buildFamilyView(filtered);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      {/* Header */}
      <div className="bg-[#1A4D2E] text-white px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 sm:w-7 sm:h-7" />
              <h1 className="font-nunito font-bold text-2xl sm:text-3xl">Gestao de Familias</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => openInvite()}
                className="bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-xl px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all"
                data-testid="invite-whatsapp-btn"
              >
                <Send className="w-4 h-4" /> Convidar
              </button>
              <button
                onClick={openCreateForm}
                className="bg-white/20 hover:bg-white/30 text-white rounded-xl px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all"
                data-testid="create-parent-btn"
              >
                <UserPlus className="w-4 h-4" /> Cadastrar
              </button>
              <button
                onClick={handleMigrateFamilies}
                className="bg-[#F4D35E] hover:bg-[#e8c74a] text-[#1A4D2E] rounded-xl px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all"
                data-testid="migrate-families-btn"
              >
                <Link2 className="w-4 h-4" /> Unificar
              </button>
            </div>
          </div>
          <p className="font-inter text-white/70 text-sm mt-1">
            {parents.length} familia(s) cadastrada(s)
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-4 sm:py-6 space-y-3">
        {/* Invite Modal */}
        {showInvite && (
          <Card className="bg-white rounded-2xl p-5 shadow-lg border-2 border-[#25D366]" data-testid="invite-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] flex items-center gap-2">
                <Send className="w-5 h-5 text-[#25D366]" /> Convidar pelo WhatsApp
              </h3>
              <button onClick={() => setShowInvite(false)} className="text-gray-400 hover:text-gray-600" data-testid="close-invite"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Celular do convidado *</label>
                <input
                  data-testid="invite-phone"
                  type="tel"
                  inputMode="numeric"
                  value={invitePhone}
                  onChange={e => setInvitePhone(e.target.value)}
                  placeholder="11999001122"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#25D366] outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Mensagem</label>
                <textarea
                  data-testid="invite-message"
                  value={inviteMsg}
                  onChange={e => setInviteMsg(e.target.value)}
                  rows={5}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#25D366] outline-none resize-none"
                />
              </div>

              <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <span className="text-xs text-gray-500 truncate flex-1">{CADASTRO_LINK}</span>
                <button onClick={copyInviteLink} className="text-[#1A4D2E] hover:bg-[#E1ECE5] rounded-lg px-2 py-1 text-xs font-semibold flex items-center gap-1 transition-all" data-testid="copy-link-btn">
                  {linkCopied ? <><Check className="w-3.5 h-3.5" /> Copiado</> : <><Copy className="w-3.5 h-3.5" /> Copiar link</>}
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowInvite(false)} className="px-4 py-2 text-sm text-gray-500">Cancelar</button>
              <button onClick={sendInviteWhatsApp} data-testid="send-invite-btn"
                className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-5 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> Enviar via WhatsApp
              </button>
            </div>
          </Card>
        )}

        {/* Search */}
        <div className="relative" data-testid="search-parents">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nome, celular ou filho..."
            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent outline-none shadow-sm"
            data-testid="search-input" />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>}
        </div>

        {/* Create Form - Family Registration */}
        {showCreateForm && (
          <Card ref={createRef} className="bg-white rounded-2xl p-5 shadow-lg border-2 border-emerald-500" data-testid="create-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-nunito font-bold text-lg text-[#1A4D2E] flex items-center gap-2">
                <UserPlus className="w-5 h-5" /> Cadastrar Nova Familia
              </h3>
              <button onClick={() => setShowCreateForm(false)} className="text-gray-400 hover:text-gray-600" data-testid="close-create-form"><X className="w-5 h-5" /></button>
            </div>
            <FormularioFamilia
              onSubmit={handleCreate}
              submitting={saving}
              submitLabel="Cadastrar Familia"
            />
          </Card>
        )}

        {/* Edit Form - Full Family Editor */}
        {editingParent && (
          <Card ref={editRef} className="bg-white rounded-2xl p-5 shadow-lg border-2 border-[#1A4D2E]" data-testid="edit-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">Editar Familia</h3>
              <button onClick={closeEdit} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            {/* Responsaveis */}
            <div className="mb-5">
              <h4 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-1"><User className="w-3.5 h-3.5" /> Responsaveis</h4>
              {editingParent.editResps.map((r, i) => (
                <div key={i} className={`space-y-2 ${i > 0 ? 'mt-3 pt-3 border-t border-gray-100' : ''}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase">{i === 0 ? 'Principal' : `Responsavel ${i + 1}`}</span>
                    {i > 0 && <button onClick={() => removeEditResp(i)} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid={`edit-resp-nome-${i}`} value={r.nome || ''} onChange={e => updateEditResp(i, 'nome', e.target.value)}
                      placeholder="Nome" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A4D2E] outline-none" />
                    <input data-testid={`edit-resp-cel-${i}`} value={r.celular || ''} onChange={e => updateEditResp(i, 'celular', e.target.value)}
                      placeholder="Celular" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A4D2E] outline-none" />
                    <select data-testid={`edit-resp-parentesco-${i}`} value={r.parentesco || ''} onChange={e => updateEditResp(i, 'parentesco', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A4D2E] outline-none">
                      <option value="">Parentesco</option><option value="Pai">Pai</option><option value="Mãe">Mae</option>
                      <option value="Avô">Avo</option><option value="Avó">Avo(a)</option><option value="Tio(a)">Tio(a)</option><option value="Outro">Outro</option>
                    </select>
                    <input data-testid={`edit-resp-email-${i}`} type="email" value={r.email || ''} onChange={e => updateEditResp(i, 'email', e.target.value)}
                      placeholder="Email" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A4D2E] outline-none" />
                  </div>
                </div>
              ))}
              {editingParent.editResps.length < 3 && (
                <button onClick={addEditResp} className="mt-2 text-[#1A4D2E] text-xs font-semibold flex items-center gap-1 hover:underline"><Plus className="w-3.5 h-3.5" /> Adicionar responsavel</button>
              )}
            </div>

            {/* Filhos */}
            <div className="mb-5">
              <h4 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-1"><Baby className="w-3.5 h-3.5" /> Filhos</h4>
              {editingParent.editFilhos.map((f, i) => (
                <div key={f.id || i} className={`space-y-2 ${i > 0 ? 'mt-3 pt-3 border-t border-gray-100' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Photo */}
                      <div className="relative shrink-0">
                        {f.foto_url ? (
                          <img src={f.foto_url.startsWith('http') ? f.foto_url : `${API.replace('/api','')}${f.foto_url}`} alt={f.nome}
                            className="w-12 h-12 rounded-xl object-cover border border-gray-200"
                            onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.className = 'hidden'; }} />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center border border-dashed border-gray-300">
                            <Camera className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                        <input type="file" accept="image/*" id={`photo-edit-${f.id}`} className="hidden"
                          onChange={e => { if (e.target.files[0] && f.id) handleUploadPhoto(f.id, e.target.files[0]); }} />
                        {f.id && (
                          <button onClick={() => document.getElementById(`photo-edit-${f.id}`)?.click()}
                            className="absolute -bottom-1 -right-1 bg-[#F4D35E] text-[#1A4D2E] rounded-full p-1 shadow-md hover:bg-[#e8c74a]">
                            <Upload className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                      <span className="text-[10px] font-bold text-gray-400 uppercase">Filho(a) {i + 1}</span>
                    </div>
                    <button onClick={() => removeEditFilho(i)} className="text-red-400 hover:text-red-600" data-testid={`remove-child-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid={`edit-filho-nome-${i}`} value={f.nome || ''} onChange={e => updateEditFilho(i, 'nome', e.target.value)}
                      placeholder="Nome" className="col-span-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A4D2E] outline-none" />
                    <input data-testid={`edit-filho-nasc-${i}`} type="date" value={f.data_nascimento || ''} onChange={e => updateEditFilho(i, 'data_nascimento', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A4D2E] outline-none" />
                    <select data-testid={`edit-filho-sexo-${i}`} value={f.sexo || 'M'} onChange={e => updateEditFilho(i, 'sexo', e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A4D2E] outline-none">
                      <option value="M">Masculino</option><option value="F">Feminino</option>
                    </select>
                    <input data-testid={`edit-filho-cel-${i}`} value={f.celular_crianca || ''} onChange={e => updateEditFilho(i, 'celular_crianca', e.target.value)}
                      placeholder="Celular crianca" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A4D2E] outline-none" />
                    <input data-testid={`edit-filho-obs-${i}`} value={f.observacoes || ''} onChange={e => updateEditFilho(i, 'observacoes', e.target.value)}
                      placeholder="Observacoes" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A4D2E] outline-none" />
                  </div>
                </div>
              ))}
              {/* Add child */}
              {!showAddChildEdit ? (
                <button onClick={() => setShowAddChildEdit(true)} className="mt-2 text-[#1A4D2E] text-xs font-semibold flex items-center gap-1 hover:underline" data-testid="add-child-edit-btn">
                  <Plus className="w-3.5 h-3.5" /> Adicionar filho(a)
                </button>
              ) : (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                  <span className="text-[10px] font-bold text-[#1A4D2E] uppercase">Novo Filho(a)</span>
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="new-child-edit-nome" value={newChildEdit.nome} onChange={e => setNewChildEdit(p => ({ ...p, nome: e.target.value }))}
                      placeholder="Nome" className="col-span-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                    <input data-testid="new-child-edit-nasc" type="date" value={newChildEdit.data_nascimento} onChange={e => setNewChildEdit(p => ({ ...p, data_nascimento: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                    <select data-testid="new-child-edit-sexo" value={newChildEdit.sexo} onChange={e => setNewChildEdit(p => ({ ...p, sexo: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                      <option value="M">Masculino</option><option value="F">Feminino</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowAddChildEdit(false)} className="flex-1 py-2 rounded-lg text-xs text-gray-500 border border-gray-200">Cancelar</button>
                    <button onClick={handleAddChildEdit} disabled={saving}
                      className="flex-1 bg-[#F4D35E] text-[#1A4D2E] py-2 rounded-lg font-bold text-xs disabled:opacity-50" data-testid="confirm-add-child-edit">Adicionar</button>
                  </div>
                </div>
              )}
            </div>

            {/* Password */}
            <div className="mb-4">
              <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Alterar Senha</label>
              <div className="relative">
                <input data-testid="edit-password" type={showPassword ? 'text' : 'password'} value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm pr-10 focus:ring-2 focus:ring-[#1A4D2E] outline-none" placeholder="Deixe vazio para manter" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={closeEdit} className="px-4 py-2 text-sm text-gray-500">Cancelar</button>
              <button onClick={handleSave} disabled={saving} data-testid="save-parent-btn"
                className="bg-[#1A4D2E] hover:bg-[#163f25] disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
                <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </Card>
        )}

        {/* List */}
        {items.length === 0 ? (
          <Card className="bg-white rounded-2xl p-12 text-center shadow-md" data-testid="empty-state">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{q ? 'Nenhum resultado encontrado' : 'Nenhum responsavel cadastrado'}</p>
            {!q && (
              <button onClick={openCreateForm} className="mt-4 bg-[#1A4D2E] text-white px-5 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2" data-testid="create-first-btn">
                <UserPlus className="w-4 h-4" /> Cadastrar primeiro responsavel
              </button>
            )}
          </Card>
        ) : items.map(item => {
          const p = item.parent;
          const responsaveis = p.responsaveis || [{ nome: p.nome, celular: p.celular, parentesco: p.parentesco, email: p.email }];
          return (
            <Card key={p.id} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5] hover:shadow-md transition-all" data-testid={`parent-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* Family name - all responsaveis */}
                  {responsaveis.map((r, i) => (
                    <div key={i} className={`${i > 0 ? 'mt-2 pt-2 border-t border-gray-100' : ''}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-nunito font-bold text-base text-[#1A4D2E]">{r.nome || 'Sem nome'}</h4>
                        {r.parentesco && <span className="bg-[#E1ECE5] text-[#1A4D2E] px-1.5 py-0.5 rounded text-[10px] font-bold">{r.parentesco}</span>}
                        {i === 0 && <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded text-[10px] font-bold">Portal</span>}
                      </div>
                      {r.celular && <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {r.celular}</p>}
                      {r.email && <p className="text-xs text-gray-400 mt-0.5">{r.email}</p>}
                    </div>
                  ))}
                  {/* Children */}
                  {p.filhos?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {p.filhos.map((f, i) => (
                        <span key={i} className="bg-[#F2F7F4] text-[#1A4D2E] px-2 py-0.5 rounded text-xs font-semibold">
                          {f.nome}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {p.celular && (
                    <button onClick={() => { const num = p.celular.replace(/\D/g, ''); window.open(`https://wa.me/55${num.startsWith('55') ? num.slice(2) : num}`, '_blank'); }}
                      className="bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-lg px-2.5 py-1.5 text-xs font-semibold flex items-center gap-1" data-testid={`whatsapp-${p.id}`}>
                      <MessageCircle className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => openEdit(p)} data-testid={`edit-parent-${p.id}`}
                    className="text-gray-400 hover:text-[#1A4D2E] hover:bg-[#F2F7F4] rounded-full p-2 transition-all"><Pencil className="w-4 h-4" /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-parent-${p.id}`}
                        className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full p-2 transition-all"><Trash2 className="w-4 h-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Excluir familia</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir a familia de <strong>{p.nome}</strong>? O login sera removido permanentemente.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(p)} className="bg-red-500 hover:bg-red-600">Excluir</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Navigation active="" />
    </div>
  );
};

export default GestaoPais;
